import React, { useMemo } from 'react'
import DOMPurify from 'dompurify'

type RichTextContentProps = {
  html: string
  className?: string
}

function preprocessHtml(html: string) {
  if (typeof window === 'undefined') return html
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const fonts = doc.querySelectorAll('font')
  fonts.forEach(font => {
    const span = document.createElement('span')
    if (font.color) span.style.color = font.color
    if (font.face) span.style.fontFamily = font.face
    if (font.size) {
      const sizeMap: Record<string, string> = {
        '1': 'x-small', '2': 'small', '3': 'medium',
        '4': 'large', '5': 'x-large', '6': 'xx-large', '7': 'xxx-large'
      }
      span.style.fontSize = sizeMap[font.size] || 'inherit'
    }
    while (font.firstChild) {
      span.appendChild(font.firstChild)
    }
    font.parentNode?.replaceChild(span, font)
  })
  return doc.body.innerHTML
}

/** Shared, responsive display contract for the HTML produced by RichTextEditor. */
const RichTextContent: React.FC<RichTextContentProps> = ({ html, className = '' }) => {
  const sanitizedHtml = useMemo(() => {
    const preprocessed = preprocessHtml(html || '')
    return DOMPurify.sanitize(preprocessed, {
      ALLOWED_TAGS: [
        'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
        'blockquote', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'pre', 'code', 'hr',
        'u', 's', 'strike'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'src', 'alt', 'title', 'width', 'height'],
    })
  }, [html])

  return (
    <div
      className={`rich-text-content max-w-none overflow-x-auto text-inherit [&_a]:text-primary-400 [&_a]:underline [&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-primary-500 [&_blockquote]:pl-4 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol_ol]:list-[lower-alpha] [&_ol_ul]:list-[circle] [&_ul_ol]:list-[lower-alpha] [&_ul_ul]:list-[circle] [&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-black/30 [&_pre]:p-4 [&_code]:rounded [&_code]:bg-dark-700 [&_code]:px-1 [&_img]:my-4 [&_img]:max-w-full [&_hr]:my-6 [&_hr]:border-dark-600 [&_table]:my-4 [&_table]:min-w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-lg [&_table]:border [&_table]:border-dark-600 [&_thead]:bg-dark-700/70 [&_tr]:border-b [&_tr]:border-dark-700 [&_th]:whitespace-nowrap [&_th]:border [&_th]:border-dark-600 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-gray-100 [&_td]:border [&_td]:border-dark-700 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  )
}

export default RichTextContent
